import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NgClass } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import packageJson from 'src/../package.json';
import { GameManager, gameManager } from 'src/app/game/businesslogic/GameManager';
import { settingsManager } from 'src/app/game/businesslogic/SettingsManager';
import { storageManager } from 'src/app/game/businesslogic/StorageManager';
import { ObjectiveData } from 'src/app/game/model/data/ObjectiveData';
import { ScenarioData } from 'src/app/game/model/data/ScenarioData';
import { Monster } from 'src/app/game/model/Monster';
import { ObjectiveContainer } from 'src/app/game/model/ObjectiveContainer';
import { SettingMenuComponent } from 'src/app/ui/header/menu/settings/setting/setting';
import { GhsLabelDirective } from 'src/app/ui/helper/label';
import { ghsDialogClosingHelper } from 'src/app/ui/helper/Static';
import { environment } from 'src/environments/environment';

export type FEEDBACK_ISSUE_TYPE = 'abilityCard' | 'monsterStat' | 'characterStat' | 'artwork' | 'software' | 'feedback' | 'automatic';

@Component({
  imports: [NgClass, FormsModule, SettingMenuComponent, GhsLabelDirective],
  selector: 'ghs-feedback-dialog',
  templateUrl: './feedback-dialog.html',
  styleUrls: ['./feedback-dialog.scss']
})
export class FeedbackDialogComponent {
  dialogRef = inject(DialogRef);

  gameManager: GameManager = gameManager;
  branded = environment.branded;

  issueTypes: FEEDBACK_ISSUE_TYPE[] = ['abilityCard', 'monsterStat', 'characterStat', 'artwork', 'software', 'feedback'];

  form: string = 'issue';
  issueType: FEEDBACK_ISSUE_TYPE = 'abilityCard';
  issuerText: string = '';
  automaticText: string = '';

  error: Error | undefined = inject(DIALOG_DATA);

  constructor() {
    if (!!this.error) {
      this.automaticText = settingsManager.getLabel('tools.feedback.reportIssue.errorText', [this.error.message, this.error.stack || '']);
      this.issueTypes.push('automatic');
      this.issueType = 'automatic';
    }
  }

  scenarioMail(scenarioName: string, index: string, notes: string): string {
    let mailto = 'mailto:scenario@gloomhaven-secretariat.de';

    const scenario = new ScenarioData();

    scenario.name = scenarioName;
    scenario.index = index;
    scenario.monsters = gameManager.game.figures.filter((figure) => figure instanceof Monster).map((figure) => (figure as Monster).name);
    scenario.allies = gameManager.game.figures
      .filter((figure) => figure instanceof Monster && figure.isAlly)
      .map((figure) => (figure as Monster).name);
    scenario.allied = gameManager.game.figures
      .filter((figure) => figure instanceof Monster && figure.isAllied)
      .map((figure) => (figure as Monster).name);
    scenario.drawExtra = gameManager.game.figures
      .filter((figure) => figure instanceof Monster && figure.drawExtra)
      .map((figure) => (figure as Monster).name);
    scenario.objectives = gameManager.game.figures
      .filter((figure) => figure instanceof ObjectiveContainer)
      .map((figure) => {
        if (figure.objectiveId) {
          const objectiveData = gameManager.objectiveManager.objectiveDataByObjectiveIdentifier(figure.objectiveId);
          if (objectiveData) {
            return objectiveData;
          }
        }
        const objective = figure as ObjectiveContainer;
        return new ObjectiveData(objective.name, objective.health, objective.escort, -1, objective.marker, [], objective.initiative);
      });
    scenario.rooms = gameManager.game.scenario?.rooms || [];
    scenario.marker = gameManager.game.scenario?.marker || '';
    scenario.rules = gameManager.game.scenario?.rules || [];
    scenario.edition = gameManager.game.scenario?.edition || gameManager.currentEdition();

    mailto +=
      '?subject=Submit Scenario #' +
      scenario.index +
      ' ' +
      scenario.name +
      ' (' +
      settingsManager.getLabel('data.edition.' + scenario.edition) +
      ')';

    mailto += '&body=' + notes + '%0D%0A%0D%0AJSON:%0D%0A' + JSON.stringify(scenario, undefined, 2);

    return mailto;
  }

  issueMail(type: string, text: string): string {
    let mailto = 'mailto:issue@gloomhaven-secretariat.de';

    if (type === 'feedback') {
      mailto = 'mailto:feedback@gloomhaven-secretariat.de';
    }

    mailto +=
      '?subject=[GHS v' + packageJson.version + '] ' + settingsManager.getLabel('tools.feedback.reportIssue.type.' + type + '.subject');

    if (this.automaticText) {
      text += (text ? '\n\n' : '') + this.automaticText;
    }

    let enviroment = 'edition: ' + gameManager.currentEdition();
    if (gameManager.game.scenario) {
      enviroment +=
        ' #' +
        gameManager.game.scenario.index +
        (gameManager.game.scenario.edition !== gameManager.currentEdition() ? ' (' + gameManager.game.scenario.edition + ')' : '');
    }

    mailto +=
      '&body=' +
      settingsManager.getLabel('tools.feedback.reportIssue.type.' + type + '.hint') +
      '%0D%0A%0D%0A' +
      text.replaceAll('\n', '%0D%0A') +
      '%0D%0A%0D%0A' +
      enviroment;

    return mailto;
  }

  async downloadDataDump() {
    try {
      const datadump: any = await storageManager.datadump();
      const downloadButton = document.createElement('a');
      downloadButton.setAttribute('href', 'data:application/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(datadump)));
      downloadButton.setAttribute('download', 'ghs-data-dump.json');
      document.body.appendChild(downloadButton);
      downloadButton.click();
      document.body.removeChild(downloadButton);
    } catch {
      console.warn('Could not read datadump');
    }
  }

  ignoreError() {
    if (this.error) {
      const errorId = this.error.message + (this.error.stack ? this.error.stack.split('\n')[0] : '');
      settingsManager.settings.feedbackErrorsIgnore = settingsManager.settings.feedbackErrorsIgnore || [];
      if (!settingsManager.settings.feedbackErrorsIgnore.includes(errorId)) {
        settingsManager.settings.feedbackErrorsIgnore.push(errorId);
        settingsManager.storeSettings();
        ghsDialogClosingHelper(this.dialogRef);
      }
    }
  }
}
